import React, { ChangeEvent } from 'react';
import { Grid, SelectChangeEvent } from '@mui/material';
import {
	CurrencyField,
	HTMLTextAreaElementExtended,
} from '@innovyze/stylovyze';
import { CostApplication, CostUnit } from '@Types';
import { useGlobalization } from '@Translations';
import type { DefectCode } from '@Types/utils.types';
import { TrenchDefectFields } from './TrenchCalculation/TrenchDefectFields';
import { getCostApplicationOptions } from './costApplicationOptions';
import { LabeledSelect } from './LabeledSelect';

export type CostPerValue = CostUnit.each | CostApplication;

interface CustomCostFieldsProps {
	initialUnitCost: number;
	costUnit: CostUnit;
	validUnitCost: string;
	viewerAccess: boolean;
	hasLengthProperty: boolean;
	system: string;
	onUnitCostChange: (
		event: ChangeEvent<HTMLInputElement | HTMLTextAreaElementExtended>,
	) => void;
	trenchCalculationEnabled: boolean;
	selectedCostApplication: CostApplication;
	onCostPerChange: (value: CostPerValue) => void;
	defectCodes: string[];
	defectCodeOptions: DefectCode[];
	defectCodesLoading?: boolean;
	onDefectCodesChange: (codes: string[]) => void;
}

export const CustomCostFields = ({
	initialUnitCost,
	costUnit,
	validUnitCost,
	viewerAccess,
	hasLengthProperty,
	system,
	onUnitCostChange,
	trenchCalculationEnabled,
	selectedCostApplication,
	onCostPerChange,
	defectCodes,
	defectCodeOptions,
	defectCodesLoading,
	onDefectCodesChange,
}: CustomCostFieldsProps) => {
	const { t } = useGlobalization();

	const costApplicationOptions = getCostApplicationOptions(
		t,
		system as 'Metric' | 'Imperial',
	);

	// "Cost per" merges the each/length unit and the pipe-length/rehab-length
	// application into a single control: each is meaningless for trench
	// costing, so there's no separate axis to expose to the user.
	const costPerOptions = [
		{ value: CostUnit.each, label: t('each') },
		...(hasLengthProperty
			? costApplicationOptions.filter(
					option =>
						trenchCalculationEnabled ||
						option.value === CostApplication.pipeLength,
				)
			: []),
	];

	const costPerValue: CostPerValue =
		costUnit === CostUnit.length ? selectedCostApplication : CostUnit.each;

	const showTrenchFields =
		trenchCalculationEnabled &&
		costUnit === CostUnit.length &&
		selectedCostApplication === CostApplication.trenchLength;

	return (
		<>
			<Grid xs={6} item>
				<CurrencyField
					key={costUnit}
					min={0}
					step={'1'}
					fullWidth
					name="UnitCost"
					precision={2}
					onChange={onUnitCostChange}
					defaultValue={initialUnitCost.toString()}
					unit={costUnit === CostUnit.length ? 'cost/m' : undefined}
					label={t('Unit Cost')}
					variant="outlined"
					helperText={validUnitCost}
					error={validUnitCost !== ''}
				/>
			</Grid>
			<Grid xs={6} item>
				<LabeledSelect
					label={t('Cost per')}
					value={costPerValue}
					dataCy="costUnitField"
					name="CostPer"
					options={costPerOptions}
					disabled={viewerAccess}
					onChange={(event: SelectChangeEvent<string>) =>
						onCostPerChange(event.target.value as CostPerValue)
					}
				/>
			</Grid>
			{showTrenchFields && (
				<TrenchDefectFields
					system={system as 'Metric' | 'Imperial'}
					defectCodes={defectCodes}
					defectCodeOptions={defectCodeOptions}
					defectCodesLoading={defectCodesLoading}
					onDefectCodesChange={onDefectCodesChange}
				/>
			)}
		</>
	);
};
